import { useEffect, useState } from 'react';
import { claveIdempotencia } from '../../../api/comun';
import { etiquetaDe, type PagoRrpp } from '../../../api/ops/listas';
import type { Liquidacion } from '../../../api/ops/portalRrpp';
import { ErrorApi } from '../../../api/problema';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Deshacer } from '../../../componentes/Deshacer';
import { Fecha } from '../../../componentes/Fecha';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { aCentimos, dinero, textoDeCentimos } from '../../../logica/dinero';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { hoyLocal } from '../../../logica/fechasCortas';
import { usePermisos } from '../../../permisos/permisos';
import { Bloque, Botonera, Botones, Caja, Dato, Fila, Vacia } from '../comun';
import { useAvisarError, useCarga } from '../uso';
import { BASE, chipLiquidacion, cuandoEs, diaHora, errorPago, FORMA_PAGO, importeAjuste, nombreRrpp, opsDe, periodo, PERMISOS, quedaPorPagar } from '../textos';
import type { ContextoLiquidaciones } from './ApartadoLiquidaciones';

/**
 * Ficha de una liquidación (10.11): Resumen (comisiones, ajustes, total y pagado de la API; por noche; ajustes con motivo),
 * Comisiones (las suyas, `listCommissions` del RRPP en el periodo con su `settlement_id`) y Pagos. Borrador: añadir un
 * ajuste (`addSettlementAdjustment`), cerrar con ventana (`closeSettlement`, no se deshace; avisa al RRPP) y descartar con
 * «Deshacer» de 5 s (`discardSettlement`). Cerrada: registrar pagos (`recordPromoterPayment`); «Deshacer» un pago anota
 * otro en negativo con su motivo, y «Corregir un pago» hace lo mismo a mano.
 */
type Forma = PagoRrpp['method'];

export function FichaLiquidacion({ ctx, liquidacionId }: { ctx: ContextoLiquidaciones; liquidacionId: string }) {
    const { props, rrpp } = ctx;
    const { negocio, movil, soloLectura } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const envio = useEnvioRetrasado();
    const [l, setL] = useState<Liquidacion | null>(null);
    const [pestana, setPestana] = useState(0);
    const [ventana, setVentana] = useState<'ajuste' | 'cerrar' | null>(null);
    const [pago, setPago] = useState<{ negativo: boolean; importe: string; forma: Forma | ''; fecha: string; referencia: string; motivo: string } | null>(null);
    const [ultimoPago, setUltimoPago] = useState<{ clave: number; importe: { amount: number; currency: string }; forma: Forma } | null>(null);
    const [ajuste, setAjuste] = useState({ importe: '', motivo: '' });
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [clave, setClave] = useState(claveIdempotencia);
    const [vuelta, setVuelta] = useState(0);

    useEffect(() => {
        let vivo = true;
        ops.getSettlement(negocio, liquidacionId)
            .then((r) => { if (vivo) { setL(r); } })
            .catch((e: unknown) => {
                if (vivo) {
                    avisarError(e, 'No se ha podido abrir la liquidación.');
                    ctx.abrir(null);
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [negocio, liquidacionId, vuelta]);

    const comisiones = useCarga(
        pestana === 1 && l !== null ? () => ops.listCommissions(negocio, { promoter_id: l.promoter_id, from: l.period_from, to: l.period_to, limit: 100 }) : null,
        [negocio, l?.id, pestana],
    );
    const eventos = useCarga(l === null ? null : () => ops.listEvents(negocio, { from: `${l.period_from}T00:00:00Z`, limit: 100 }), [negocio, l?.period_from]);
    const nombreEvento = (id: string) => {
        const e = eventos.datos?.data.find((x) => x.id === id);
        return e === undefined ? 'Evento' : `${e.name ?? ''} · ${cuandoEs(e.starts_at)}`;
    };
    const gestionar = permisos.puede(PERMISOS.liquidacionesGestionar) && !soloLectura;
    const pagar = permisos.puede(PERMISOS.pagos) && !soloLectura;
    const nombre = nombreRrpp(l === null ? undefined : rrpp.get(l.promoter_id));
    const chip = l === null ? null : chipLiquidacion(l);
    const queda = l === null ? null : quedaPorPagar(l);

    const tras = (r: Liquidacion, texto: string) => {
        setL(r);
        setVentana(null);
        avisar({ tipo: 'ok', texto });
        ctx.recargar();
    };
    const fallo = (e: unknown) => {
        if (e instanceof ErrorApi && e.estado === 412) {
            avisar({ tipo: 'error', texto: 'La liquidación ha cambiado. La hemos vuelto a leer.' });
            setVuelta((v) => v + 1);
        } else {
            avisarError(e);
        }
    };

    const confirmar = () => {
        if (l === null) {
            return;
        }
        if (ventana === 'ajuste') {
            const c = importeAjuste(ajuste.importe);
            if (c === null || ajuste.motivo.trim() === '') {
                setProbado(true);
                return;
            }
            setOcupado(true);
            ops.addSettlementAdjustment(negocio, l.id, { amount: { amount: c, currency: l.total.currency }, reason: ajuste.motivo.trim() }, etiquetaDe(l), clave)
                .then((r) => { tras(r, `Ajuste de ${dinero({ amount: c, currency: l.total.currency })} añadido.`); })
                .catch(fallo)
                .finally(() => { setOcupado(false); });
        } else if (ventana === 'cerrar') {
            setOcupado(true);
            ops.closeSettlement(negocio, l.id, etiquetaDe(l), clave)
                .then((r) => {
                    tras(r, `Liquidación cerrada: ${dinero(r.total)}. Avisamos a ${nombre}. Ahora anota el pago cuando se lo hagas.`);
                    setPestana(2);
                })
                .catch(fallo)
                .finally(() => { setOcupado(false); });
        }
    };

    const descartar = () => {
        if (l === null) {
            return;
        }
        const id = l.id;
        envio.programar({
            texto: `Borrador de ${nombre} descartado: sus comisiones vuelven a pendientes.`,
            donde: 'ficha',
            enviar: () => {
                ops.discardSettlement(negocio, id)
                    .then(() => { ctx.recargar(); ctx.abrir(null); })
                    .catch((e: unknown) => { avisarError(e, 'No se ha podido descartar el borrador.'); });
            },
        });
    };

    const registrar = () => {
        if (l === null || pago === null) {
            return;
        }
        const err = errorPago(pago.importe, pago.negativo, l);
        if (err !== null || pago.forma === '' || (pago.negativo && pago.motivo.trim() === '') || pago.fecha === '') {
            setProbado(true);
            return;
        }
        const c = aCentimos(pago.importe.replace(/^[−-]/, '')) ?? 0;
        const importe = { amount: pago.negativo ? -c : c, currency: l.total.currency };
        const forma = pago.forma;
        setOcupado(true);
        ops.recordPromoterPayment(negocio, l.id, {
            amount: importe,
            method: forma,
            paid_at: new Date(`${pago.fecha}T12:00:00`).toISOString(),
            ...(pago.referencia.trim() === '' ? {} : { reference: pago.referencia.trim() }),
            ...(pago.negativo ? { reason: pago.motivo.trim() } : {}),
        }, clave)
            .then((r) => {
                setL(r);
                setPago(null);
                setClave(claveIdempotencia());
                ctx.recargar();
                if (pago.negativo) {
                    avisar({ tipo: 'ok', texto: `Corrección de ${dinero(importe)} anotada.` });
                } else {
                    setUltimoPago({ clave: Date.now(), importe, forma });
                }
            })
            .catch((e: unknown) => { avisarError(e); })
            .finally(() => { setOcupado(false); });
    };

    /** «Deshacer» un pago recién anotado: otro pago igual en negativo, con su motivo (no se borra nada). */
    const deshacerPago = () => {
        if (l === null || ultimoPago === null) {
            return;
        }
        const p = ultimoPago;
        setUltimoPago(null);
        ops.recordPromoterPayment(negocio, l.id, { amount: { amount: -p.importe.amount, currency: p.importe.currency }, method: p.forma, paid_at: new Date().toISOString(), reason: 'Deshecho: pago anotado por error' })
            .then((r) => {
                setL(r);
                avisar({ tipo: 'ok', texto: 'Pago deshecho: queda anotado en negativo.' });
                ctx.recargar();
            })
            .catch((e: unknown) => { avisarError(e, 'No se ha podido deshacer el pago. Corrígelo con «Corregir un pago».'); });
    };

    const abrirPago = (negativo: boolean) => {
        if (l === null || queda === null) {
            return;
        }
        setProbado(false);
        setClave(claveIdempotencia());
        setPago({ negativo, importe: negativo ? '' : textoDeCentimos(Math.max(0, queda.amount)), forma: negativo ? 'cash' : '', fecha: hoyLocal(), referencia: '', motivo: '' });
    };

    if (pago !== null && l !== null && queda !== null) {
        const errImporte = probado ? errorPago(pago.importe, pago.negativo, l) : null;
        return (
            <PanelLateral
                abierto
                onCerrar={() => { setPago(null); }}
                movil={movil}
                titulo={pago.negativo ? 'Corregir un pago' : 'Registrar un pago'}
                eyebrow={`Liquidación · ${nombre}`}
                subtitulo={pago.negativo ? 'Un pago mal anotado se corrige con otro en negativo.' : `Quedan ${dinero(queda)} de ${dinero(l.total)}`}
                icono="banknote"
                accion={pago.negativo ? 'Anotar corrección' : 'Registrar pago'}
                accionIcono="banknote"
                ocupado={ocupado}
                cerrar="Volver"
                onAccion={registrar}
            >
                <div className="rv-ca-cuerpo">
                    <Bloque>
                        <Campo etiqueta={pago.negativo ? 'Importe a restar (€)' : 'Importe (€)'} valor={pago.importe} onChange={(v) => { setPago({ ...pago, importe: v }); }} modo="decimal" error={errImporte} ayuda={pago.negativo ? `Lo pagado hasta ahora: ${dinero(l.paid_total)}.` : 'Puedes pagarle en varias veces.'} />
                    </Bloque>
                    <Bloque titulo="Cómo se lo pagaste">
                        <div className={`rv-ls-opciones${movil ? ' rv-ls-opciones--movil' : ''}`} role="radiogroup" aria-label="Cómo se lo pagaste">
                            {(Object.keys(FORMA_PAGO) as Forma[]).map((k) => (
                                <OpcionRadio key={k} elegida={pago.forma === k} onElegir={() => { setPago({ ...pago, forma: k }); }} titulo={FORMA_PAGO[k][0]} icono={FORMA_PAGO[k][1]} />
                            ))}
                        </div>
                        {probado && pago.forma === '' && <Caja icono="circle-alert" tono="error">Elige cómo se lo pagaste.</Caja>}
                        <Fecha etiqueta="Fecha del pago" valor={pago.fecha} onChange={(v) => { setPago({ ...pago, fecha: v }); }} max={hoyLocal()} error={probado && pago.fecha === '' ? 'Elige la fecha.' : null} />
                        {pago.negativo && <Campo etiqueta="Motivo" valor={pago.motivo} onChange={(v) => { setPago({ ...pago, motivo: v }); }} max={300} error={probado && pago.motivo.trim() === '' ? 'Escribe el motivo.' : null} ayuda="Por ejemplo: «Anotado dos veces»." />}
                        <Campo etiqueta="Referencia (opcional)" valor={pago.referencia} onChange={(v) => { setPago({ ...pago, referencia: v }); }} max={120} ayuda="Número de transferencia o recibo." />
                        <Caja icono="info">El dinero se lo das tú, fuera de Reservaria: aquí solo queda anotado.</Caja>
                    </Bloque>
                </div>
            </PanelLateral>
        );
    }

    const accion = l === null ? '' : gestionar && l.status === 'draft' ? 'Cerrar la liquidación' : pagar && l.status === 'closed' && (queda?.amount ?? 0) > 0 ? 'Registrar un pago' : '';
    return (
        <>
            <PanelLateral
                abierto
                onCerrar={() => { ctx.abrir(null); }}
                movil={movil}
                tamano="ficha"
                titulo={l === null ? 'Liquidación' : dinero(l.total)}
                eyebrow={`Liquidación · ${nombre}`}
                subtitulo={l === null ? '' : periodo(l)}
                chip={chip?.[0]}
                chipEstado={chip?.[1]}
                icono="wallet"
                accion={accion}
                accionIcono={l?.status === 'draft' ? 'lock' : 'banknote'}
                onAccion={() => {
                    if (l?.status === 'draft') {
                        setClave(claveIdempotencia());
                        setVentana('cerrar');
                    } else {
                        abrirPago(false);
                    }
                }}
            >
                <div className="rv-ca-cuerpo">
                    <Botonera etiqueta="Partes de la liquidación" items={[{ texto: 'Resumen', icono: 'receipt' }, { texto: 'Comisiones', icono: 'list' }, { texto: 'Pagos', icono: 'banknote' }]} valor={pestana} onCambio={setPestana} />
                    {envio.pendiente !== null && (
                        <div className="rv-ca-deshacer">
                            <span>{envio.pendiente.texto}</span>
                            <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                        </div>
                    )}
                    {ultimoPago !== null && (
                        <div className="rv-ca-deshacer">
                            <span>Pago de {dinero(ultimoPago.importe)} a {nombre} anotado.</span>
                            <Deshacer onDeshacer={deshacerPago} onFin={() => { setUltimoPago(null); }} clave={ultimoPago.clave} movil={movil} icono="banknote" />
                        </div>
                    )}
                    {l === null || queda === null ? (
                        <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                    ) : (
                        <>
                            {pestana === 0 && (
                                <>
                                    <Bloque>
                                        <Dato icono="list" k="Comisiones" v={dinero(l.commissions_total)} />
                                        <Dato icono="pencil" k="Ajustes" v={dinero(l.adjustments_total)} sub={`${String(l.adjustments?.length ?? 0)} con motivo`} />
                                        <Dato icono="wallet" k="Total" v={dinero(l.total)} sub={l.status === 'closed' ? 'Congelado' : 'Al cerrarla'} />
                                        {l.status === 'closed' && <Dato icono="banknote" k="Pagado" v={dinero(l.paid_total)} sub={queda.amount > 0 ? `Quedan ${dinero(queda)}` : 'Todo pagado'} />}
                                    </Bloque>
                                    {l.status === 'closed' && <Caja icono="lock">Cerrada el {diaHora(l.closed_at)}. Avisamos a {nombre}. Una llegada deshecha después entra como corrección en la siguiente.</Caja>}
                                    {l.status === 'discarded' && <Caja icono="trash-2">Descartada: sus comisiones volvieron a pendientes.</Caja>}
                                    <Bloque titulo="Por noche">
                                        {(l.by_event ?? []).map((e, i) => (
                                            <Fila key={e.event_id ?? String(i)} baldosa="calendar" titulo={nombreEvento(e.event_id ?? '')} linea={`${String(e.count ?? 0)} comisiones`} derecha={dinero(e.amount)} />
                                        ))}
                                    </Bloque>
                                    <Bloque titulo="Ajustes">
                                        {(l.adjustments ?? []).map((a, i) => (
                                            <Fila key={`${a.created_at ?? ''}-${String(i)}`} baldosa="pencil" titulo={a.reason ?? ''} linea={diaHora(a.created_at)} derecha={dinero(a.amount)} />
                                        ))}
                                        {(l.adjustments ?? []).length === 0 && <Caja icono="info">{l.status === 'draft' ? 'Sin ajustes. Si algo no cuadra, añade uno con su motivo: queda anotado.' : 'Sin ajustes.'}</Caja>}
                                        {gestionar && l.status === 'draft' && (
                                            <Botones>
                                                <Boton tipo="secundario" icono="plus" onClick={() => { setAjuste({ importe: '', motivo: '' }); setProbado(false); setClave(claveIdempotencia()); setVentana('ajuste'); }}>Añadir un ajuste</Boton>
                                            </Botones>
                                        )}
                                    </Bloque>
                                    {gestionar && l.status === 'draft' && envio.pendiente === null && (
                                        <Botones nota="Sus comisiones vuelven a pendientes.">
                                            <Boton tipo="destructivo" icono="trash-2" onClick={descartar}>Descartar el borrador</Boton>
                                        </Botones>
                                    )}
                                </>
                            )}
                            {pestana === 1 && (
                                <>
                                    {comisiones.datos === null ? (
                                        <p className="rv-ca-nota rv-ca-nota--gris">Cargando…</p>
                                    ) : (
                                        comisiones.datos.data
                                            .filter((c) => c.settlement_id === l.id)
                                            .map((c) => (
                                                <Fila
                                                    key={c.id}
                                                    baldosa={c.kind === 'reversal' ? 'rotate-ccw' : BASE[c.basis][1]}
                                                    titulo={c.kind === 'reversal' ? `Corrección · ${BASE[c.basis][0]}` : BASE[c.basis][0]}
                                                    linea={`${nombreEvento(c.event_id)} · ${diaHora(c.created_at)}${c.night_count == null ? '' : ` · ${String(c.night_count)} esa noche`}`}
                                                    derecha={dinero(c.amount)}
                                                />
                                            ))
                                    )}
                                    <Caja icono="info">Cada comisión dice qué tarifa se aplicó. Las correcciones (una llegada deshecha) restan.</Caja>
                                </>
                            )}
                            {pestana === 2 &&
                                (l.status !== 'closed' ? (
                                    <Vacia icono="banknote" titulo="Todavía no se puede pagar" texto={l.status === 'draft' ? 'Los pagos se anotan cuando la cierres.' : 'Está descartada.'} />
                                ) : (
                                    <>
                                        <Dato icono="banknote" k="Pagado" v={`${dinero(l.paid_total)} de ${dinero(l.total)}`} sub={queda.amount > 0 ? `Quedan ${dinero(queda)}` : 'Todo pagado'} />
                                        <Bloque titulo="Pagos">
                                            {(l.payments ?? []).length === 0 ? (
                                                <Vacia icono="banknote" titulo="Sin pagos todavía" texto="Anota cada pago que le hagas, en una vez o en varias." />
                                            ) : (
                                                (l.payments ?? []).map((p, i) => {
                                                    const forma = FORMA_PAGO[p.method as Forma] as [string, 'banknote'] | undefined;
                                                    return <Fila key={p.id ?? String(i)} baldosa={forma?.[1] ?? 'wallet'} titulo={dinero(p.amount)} linea={`${forma?.[0] ?? p.method ?? ''} · ${diaHora(p.paid_at)}${p.reference == null ? '' : ` · ${p.reference}`}`} />;
                                                })
                                            )}
                                        </Bloque>
                                        {pagar && (
                                            <Botones nota="Un pago mal anotado se corrige con otro en negativo y su motivo.">
                                                {queda.amount > 0 && <Boton tipo="secundario" icono="banknote" onClick={() => { abrirPago(false); }}>Registrar un pago</Boton>}
                                                {l.paid_total.amount > 0 && <Boton tipo="secundario" icono="rotate-ccw" onClick={() => { abrirPago(true); }}>Corregir un pago</Boton>}
                                            </Botones>
                                        )}
                                    </>
                                ))}
                        </>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={ventana !== null}
                titulo={ventana === 'ajuste' ? 'Añadir un ajuste' : 'Cerrar la liquidación'}
                texto={ventana === 'ajuste' ? 'Suma o resta al total con su motivo. Queda anotado con tu nombre.' : `Se congela el total y sus comisiones pasan a liquidadas. No se puede deshacer. Avisamos a ${nombre}.`}
                accion={ventana === 'ajuste' ? 'Añadir' : 'Cerrar'}
                accionIcono={ventana === 'ajuste' ? 'plus' : 'lock'}
                ocupado={ocupado}
                ocupadoTexto={ventana === 'ajuste' ? 'Añadiendo…' : 'Cerrando…'}
                movil={movil}
                onAccion={confirmar}
                onCerrar={() => { if (!ocupado) { setVentana(null); } }}
            >
                {ventana === 'ajuste' ? (
                    <>
                        <Campo etiqueta="Importe (€)" valor={ajuste.importe} onChange={(v) => { setAjuste({ ...ajuste, importe: v }); }} max={12} error={probado && importeAjuste(ajuste.importe) === null ? 'Escribe el importe. En negativo para restar: −5,00.' : null} ayuda="En negativo para restar, por ejemplo −5,00." />
                        <Campo etiqueta="Motivo" valor={ajuste.motivo} onChange={(v) => { setAjuste({ ...ajuste, motivo: v }); }} max={300} error={probado && ajuste.motivo.trim() === '' ? 'Escribe el motivo.' : null} />
                    </>
                ) : (
                    l !== null && (
                        <div className="rv-ca-ventana-lineas">
                            <div className="rv-ca-ventana-linea"><span>RRPP</span><strong>{nombre}</strong></div>
                            <div className="rv-ca-ventana-linea"><span>Periodo</span><strong>{periodo(l)}</strong></div>
                            <div className="rv-ca-ventana-linea"><span>Comisiones</span><strong>{dinero(l.commissions_total)}</strong></div>
                            <div className="rv-ca-ventana-linea"><span>Ajustes</span><strong>{dinero(l.adjustments_total)}</strong></div>
                            <div className="rv-ca-ventana-linea"><span>Total</span><strong>{dinero(l.total)}</strong></div>
                        </div>
                    )
                )}
            </Ventana>
        </>
    );
}
